import { useState, useCallback, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Upload, FileImage, Zap, Clock, Settings2, Layers, 
  Eye, EyeOff, Trash2, Plus, Target, ChevronLeft, ChevronRight,
  Crosshair, RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as UTIF from "utif2";

export default function UploadPanel({ 
  onAnalyze, 
  onTimeLapseAnalyze, 
  isLoading,
  thresholdMethod,
  setThresholdMethod,
  uploadState,
  setUploadState
}) {
  // Use lifted state from parent for persistence
  const { 
    redFile, greenFile, previewData, currentZ, 
    rois, activeRoiId, roiEnabled, isTimeLapse 
  } = uploadState;
  
  // Helper to update upload state
  const updateUploadState = useCallback((updates) => {
    setUploadState(prev => ({ ...prev, ...updates }));
  }, [setUploadState]);
  
  // Local UI state (doesn't need persistence)
  const [percentile, setPercentile] = useState(75);
  const [thresholdStrategy, setThresholdStrategy] = useState("per_frame");
  const [activeTab, setActiveTab] = useState("upload");
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [viewMode, setViewMode] = useState("merged");
  const canvasRef = useRef(null);

  // Advanced analysis options
  const [pearsonMode, setPearsonMode] = useState("union_fg");
  const [includeVolumes, setIncludeVolumes] = useState(true);
  const [volumeQuality, setVolumeQuality] = useState("medium");
  const [includeFramesData, setIncludeFramesData] = useState(true);
  const [maxFrameDim, setMaxFrameDim] = useState(512);

  const activeRoi = rois.find(r => r.id === activeRoiId) || rois[0];

  // Setters that update lifted state
  const setRedFile = useCallback((file) => updateUploadState({ redFile: file }), [updateUploadState]);
  const setGreenFile = useCallback((file) => updateUploadState({ greenFile: file }), [updateUploadState]);
  const setPreviewData = useCallback((data) => updateUploadState({ previewData: data }), [updateUploadState]);
  const setCurrentZ = useCallback((z) => updateUploadState({ currentZ: z }), [updateUploadState]);
  const setRois = useCallback((roisOrFn) => {
    if (typeof roisOrFn === 'function') {
      setUploadState(prev => ({ ...prev, rois: roisOrFn(prev.rois) }));
    } else {
      updateUploadState({ rois: roisOrFn });
    }
  }, [setUploadState, updateUploadState]);
  const setActiveRoiId = useCallback((id) => updateUploadState({ activeRoiId: id }), [updateUploadState]);
  const setRoiEnabled = useCallback((enabled) => updateUploadState({ roiEnabled: enabled }), [updateUploadState]);
  const setIsTimeLapse = useCallback((val) => updateUploadState({ isTimeLapse: val }), [updateUploadState]);

  // Load demo files
  const loadDemoFiles = async () => {
    setLoadingDemo(true);
    try {
      const baseUrl = window.location.origin;
      const redUrl = isTimeLapse 
        ? `${baseUrl}/samples/sample_red_timelapse.tif`
        : `${baseUrl}/samples/sample_red_3d.tif`;
      const greenUrl = isTimeLapse
        ? `${baseUrl}/samples/sample_green_timelapse.tif`
        : `${baseUrl}/samples/sample_green_3d.tif`;

      const [redRes, greenRes] = await Promise.all([
        fetch(redUrl),
        fetch(greenUrl)
      ]);

      const redBlob = await redRes.blob();
      const greenBlob = await greenRes.blob();

      const redDemoFile = new File([redBlob], isTimeLapse ? "sample_red_timelapse.tif" : "sample_red_3d.tif", { type: "image/tiff" });
      const greenDemoFile = new File([greenBlob], isTimeLapse ? "sample_green_timelapse.tif" : "sample_green_3d.tif", { type: "image/tiff" });

      setRedFile(redDemoFile);
      setGreenFile(greenDemoFile);
      
      // Load preview data
      await loadPreview(redDemoFile, greenDemoFile);
    } catch (error) {
      console.error("Failed to load demo files:", error);
    } finally {
      setLoadingDemo(false);
    }
  };

  // Load preview from TIFF files
  const loadPreview = async (red, green) => {
    try {
      const readTiff = async (file) => {
        const buffer = await file.arrayBuffer();
        const ifds = UTIF.decode(buffer);
        
        // Get all pages/slices
        const pages = [];
        for (let i = 0; i < ifds.length; i++) {
          UTIF.decodeImage(buffer, ifds[i]);
          const rgba = UTIF.toRGBA8(ifds[i]); // Returns Uint8Array directly
          pages.push({
            data: rgba, // This is already the Uint8Array
            width: ifds[i].width,
            height: ifds[i].height
          });
        }
        return pages;
      };

      const [redPages, greenPages] = await Promise.all([
        readTiff(red),
        readTiff(green)
      ]);

      console.log("Loaded TIFF data:", {
        redSlices: redPages.length,
        greenSlices: greenPages.length,
        redSize: redPages[0]?.data?.length,
        greenSize: greenPages[0]?.data?.length,
        width: redPages[0]?.width,
        height: redPages[0]?.height
      });

      const depth = Math.min(redPages.length, greenPages.length);
      const width = redPages[0]?.width || 128;
      const height = redPages[0]?.height || 128;

      setPreviewData({
        width,
        height,
        depth,
        redPages,
        greenPages,
        hasData: true
      });
      
      // Start at middle slice
      setCurrentZ(Math.floor(depth / 2));
    } catch (error) {
      console.error("Preview load error:", error);
      // Set fallback preview
      setPreviewData({
        width: 128,
        height: 128,
        depth: 20,
        hasData: false
      });
    }
  };

  // Draw preview canvas with actual TIFF data
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const canvasW = canvas.width;
    const canvasH = canvas.height;
    
    // Clear with dark background
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(0, 0, canvasW, canvasH);

    if (!previewData?.hasData || !previewData?.redPages || !previewData?.greenPages) {
      // No data - show placeholder
      ctx.fillStyle = "#64748b";
      ctx.font = "14px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Load files to preview", canvasW / 2, canvasH / 2 - 10);
      ctx.fillText("ROI will be shown here", canvasW / 2, canvasH / 2 + 10);
      return;
    }

    const { redPages, greenPages, width, height, depth } = previewData;
    const zIndex = Math.min(Math.max(0, currentZ), depth - 1);
    
    const redPage = redPages[zIndex];
    const greenPage = greenPages[zIndex];
    
    if (!redPage?.data || !greenPage?.data) return;

    // Create image data for the canvas
    const scale = Math.min(canvasW / width, canvasH / height);
    const scaledW = Math.floor(width * scale);
    const scaledH = Math.floor(height * scale);
    const offsetX = Math.floor((canvasW - scaledW) / 2);
    const offsetY = Math.floor((canvasH - scaledH) / 2);

    // Create temporary canvas for the image
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext("2d");
    const imageData = tempCtx.createImageData(width, height);

    // RGBA data from UTIF - each pixel has 4 values (R, G, B, A)
    const redData = redPage.data;
    const greenData = greenPage.data;

    // Merge red and green channels
    // For RGB TIFFs: red file stores intensity in R channel (index 0)
    //               green file stores intensity in G channel (index 1)
    // For grayscale TIFFs: both R, G, B have the same value
    for (let i = 0; i < width * height; i++) {
      const srcIdx = i * 4;  // Source index in RGBA array
      const dstIdx = i * 4;  // Destination index in imageData
      
      // Get red channel intensity - from R component of red file
      // If grayscale, R=G=B so taking R works
      // If RGB, taking R gets the red channel
      const redVal = redData[srcIdx];
      
      // Get green channel intensity - check if it's RGB (G differs from R) or grayscale
      // For RGB TIFF green file: green is in G channel (srcIdx + 1)
      // For grayscale TIFF: R=G=B, so we can use R (srcIdx)
      const greenRaw = greenData[srcIdx];     // R component
      const greenG = greenData[srcIdx + 1];   // G component
      
      // Use G channel if it has significant data and differs from R (indicates RGB)
      // Otherwise use R channel (grayscale case)
      const greenVal = (greenG > 0 && greenG !== greenRaw) ? greenG : greenRaw;
      
      if (viewMode === "merged") {
        imageData.data[dstIdx] = redVal;       // R
        imageData.data[dstIdx + 1] = greenVal; // G
        imageData.data[dstIdx + 2] = 0;        // B
        imageData.data[dstIdx + 3] = 255;      // A
      } else if (viewMode === "red") {
        imageData.data[dstIdx] = redVal;
        imageData.data[dstIdx + 1] = 0;
        imageData.data[dstIdx + 2] = 0;
        imageData.data[dstIdx + 3] = 255;
      } else if (viewMode === "green") {
        imageData.data[dstIdx] = 0;
        imageData.data[dstIdx + 1] = greenVal;
        imageData.data[dstIdx + 2] = 0;
        imageData.data[dstIdx + 3] = 255;
      }
    }

    tempCtx.putImageData(imageData, 0, 0);
    
    // Draw scaled image to main canvas
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tempCanvas, offsetX, offsetY, scaledW, scaledH);
    
    // Draw ROI overlay if enabled
    if (roiEnabled && activeRoi) {
      // Convert range format to position/size
      const roiX = offsetX + (activeRoi.xRange[0] / 100) * scaledW;
      const roiY = offsetY + (activeRoi.yRange[0] / 100) * scaledH;
      const roiW = ((activeRoi.xRange[1] - activeRoi.xRange[0]) / 100) * scaledW;
      const roiH = ((activeRoi.yRange[1] - activeRoi.yRange[0]) / 100) * scaledH;
      
      // Fill with semi-transparent overlay outside ROI
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      // Top
      ctx.fillRect(offsetX, offsetY, scaledW, roiY - offsetY);
      // Bottom
      ctx.fillRect(offsetX, roiY + roiH, scaledW, scaledH - (roiY - offsetY) - roiH);
      // Left
      ctx.fillRect(offsetX, roiY, roiX - offsetX, roiH);
      // Right
      ctx.fillRect(roiX + roiW, roiY, scaledW - (roiX - offsetX) - roiW, roiH);
      
      // Draw ROI rectangle border
      ctx.strokeStyle = activeRoi.color || "#3b82f6";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(roiX, roiY, roiW, roiH);
      ctx.setLineDash([]);
      
      // Draw corner handles
      const handleSize = 8;
      ctx.fillStyle = activeRoi.color || "#3b82f6";
      ctx.fillRect(roiX - handleSize/2, roiY - handleSize/2, handleSize, handleSize);
      ctx.fillRect(roiX + roiW - handleSize/2, roiY - handleSize/2, handleSize, handleSize);
      ctx.fillRect(roiX - handleSize/2, roiY + roiH - handleSize/2, handleSize, handleSize);
      ctx.fillRect(roiX + roiW - handleSize/2, roiY + roiH - handleSize/2, handleSize, handleSize);
    }
    
  }, [previewData, currentZ, viewMode, roiEnabled, activeRoi, rois]);

  const handleDrop = useCallback((e, channel) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.tif') || file.name.endsWith('.tiff'))) {
      if (channel === "red") {
        // Update red file and check if green exists to load preview
        setUploadState(prev => {
          const newState = { ...prev, redFile: file };
          if (prev.greenFile) {
            loadPreview(file, prev.greenFile);
          }
          return newState;
        });
      } else {
        // Update green file and check if red exists to load preview
        setUploadState(prev => {
          const newState = { ...prev, greenFile: file };
          if (prev.redFile) {
            loadPreview(prev.redFile, file);
          }
          return newState;
        });
      }
    }
  }, [setUploadState]);

  const handleFileSelect = useCallback((e, channel) => {
    const file = e.target.files[0];
    if (file) {
      if (channel === "red") {
        setUploadState(prev => {
          const newState = { ...prev, redFile: file };
          if (prev.greenFile) {
            loadPreview(file, prev.greenFile);
          }
          return newState;
        });
      } else {
        setUploadState(prev => {
          const newState = { ...prev, greenFile: file };
          if (prev.redFile) {
            loadPreview(prev.redFile, file);
          }
          return newState;
        });
      }
    }
    // Reset input value to allow re-selecting same file
    e.target.value = '';
  }, [setUploadState]);

  const updateRoi = (id, updates) => {
    setRois(prev => prev.map(roi => 
      roi.id === id ? { ...roi, ...updates } : roi
    ));
  };

  const addRoi = () => {
    const newId = `roi-${Date.now()}`;
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
    setRois(prev => [...prev, {
      id: newId,
      name: `ROI ${prev.length + 1}`,
      visible: true,
      xRange: [10 + (prev.length * 5) % 30, 50 + (prev.length * 5) % 30],
      yRange: [10 + (prev.length * 5) % 30, 50 + (prev.length * 5) % 30],
      zRange: [0, 100],
      color: colors[prev.length % colors.length]
    }]);
    setActiveRoiId(newId);
    setRoiEnabled(true);
  };

  const deleteRoi = (id) => {
    setRois(prev => prev.filter(r => r.id !== id));
    if (activeRoiId === id) {
      setActiveRoiId(rois[0]?.id);
    }
  };

  // Clear all uploaded files and reset preview
  const handleClearAll = useCallback(() => {
    setUploadState({
      redFile: null,
      greenFile: null,
      previewData: null,
      currentZ: 0,
      rois: [
        {
          id: "roi-1",
          name: "Center ROI",
          visible: true,
          xRange: [25, 75],
          yRange: [25, 75],
          zRange: [0, 100],
          color: "#3b82f6"
        }
      ],
      activeRoiId: "roi-1",
      roiEnabled: false,
      isTimeLapse: false
    });
  }, [setUploadState]);

  const handleSubmit = () => {
    if (!redFile || !greenFile) return;
    
    const qualityMap = {
      low:   { maxXYSize: 192, maxVoxels: 800000 },
      medium:{ maxXYSize: 256, maxVoxels: 1500000 },
      high:  { maxXYSize: 384, maxVoxels: 2500000 }
    };
    const q = qualityMap[volumeQuality] || qualityMap.medium;

    // Use active ROI if enabled
    const roi = roiEnabled && activeRoi ? activeRoi : null;

    const options = {
      thresholdMethod,
      percentile,
      strategy: thresholdStrategy,
      pearsonMode,
      includeVolumes,
      maxXYSize: q.maxXYSize,
      maxVoxels: q.maxVoxels,
      includeFramesData,
      maxFrameDim,
      // ROI options - convert range format to min/max
      roiEnabled: !!roi,
      roiXMin: roi ? roi.xRange[0] : 0,
      roiXMax: roi ? roi.xRange[1] : 100,
      roiYMin: roi ? roi.yRange[0] : 0,
      roiYMax: roi ? roi.yRange[1] : 100,
      roiZMin: roi ? roi.zRange[0] : 0,
      roiZMax: roi ? roi.zRange[1] : 100
    };
    
    if (isTimeLapse) {
      onTimeLapseAnalyze(redFile, greenFile, options);
    } else {
      onAnalyze(redFile, greenFile, options);
    }
  };

  const canSubmit = redFile && greenFile && !isLoading;
  const maxZ = previewData?.depth || 20;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto"
    >
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left Panel - Upload & Settings */}
        <div className="space-y-6">
          <div className="metric-card p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="upload" className="gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Files
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-2">
                  <Settings2 className="w-4 h-4" />
                  Analysis Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4">
                {/* File Upload Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <DropZone
                    channel="red"
                    file={redFile}
                    onDrop={(e) => handleDrop(e, "red")}
                    onFileSelect={(e) => handleFileSelect(e, "red")}
                    onClear={() => setRedFile(null)}
                  />
                  <DropZone
                    channel="green"
                    file={greenFile}
                    onDrop={(e) => handleDrop(e, "green")}
                    onFileSelect={(e) => handleFileSelect(e, "green")}
                    onClear={() => setGreenFile(null)}
                  />
                </div>

                {/* Time-lapse Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm">Time-lapse Mode</Label>
                  </div>
                  <Switch
                    checked={isTimeLapse}
                    onCheckedChange={setIsTimeLapse}
                    data-testid="timelapse-toggle"
                  />
                </div>

                {/* Load Demo Button */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={loadDemoFiles}
                    disabled={loadingDemo}
                    className="gap-2"
                    data-testid="load-demo-btn"
                  >
                    {loadingDemo ? (
                      <>
                        <div className="spinner w-4 h-4" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        Load Demo
                      </>
                    )}
                  </Button>
                  
                  {/* Clear/Reload Button */}
                  <Button
                    variant="outline"
                    onClick={handleClearAll}
                    disabled={!redFile && !greenFile}
                    className="gap-2"
                    data-testid="clear-all-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Clear All
                  </Button>
                </div>

                {/* Analyze Button */}
                <Button
                  size="lg"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="w-full py-6 text-lg rounded-xl gap-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  data-testid="analyze-btn"
                >
                  {isLoading ? (
                    <>
                      <div className="spinner w-5 h-5" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      {isTimeLapse ? "Analyze Time-lapse" : "Analyze Stack"}
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="settings" className="space-y-4">
                {/* Threshold Method */}
                <div className="space-y-2">
                  <Label className="text-sm">Threshold Method</Label>
                  <Select value={thresholdMethod} onValueChange={setThresholdMethod}>
                    <SelectTrigger data-testid="threshold-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="otsu">Otsu (Auto)</SelectItem>
                      <SelectItem value="percentile">Percentile</SelectItem>
                      <SelectItem value="kmeans">K-means (ML)</SelectItem>
                      <SelectItem value="costes">Costes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Percentile Slider */}
                {thresholdMethod === "percentile" && (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-sm">Percentile</Label>
                      <span className="font-mono text-sm text-muted-foreground">{percentile}%</span>
                    </div>
                    <Slider
                      value={[percentile]}
                      onValueChange={([v]) => setPercentile(v)}
                      min={50}
                      max={99}
                      step={1}
                    />
                  </div>
                )}

                {/* Pearson Mode */}
                <div className="space-y-2">
                  <Label className="text-sm">Pearson (r) Mode</Label>
                  <Select value={pearsonMode} onValueChange={setPearsonMode}>
                    <SelectTrigger data-testid="pearson-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="union_fg">Union foreground</SelectItem>
                      <SelectItem value="both_fg">Both-foreground</SelectItem>
                      <SelectItem value="roi_all">All pixels</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Volume Quality */}
                {!isTimeLapse && (
                  <div className="space-y-2">
                    <Label className="text-sm">3D Preview Quality</Label>
                    <Select value={volumeQuality} onValueChange={setVolumeQuality}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low (fast)</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High (slower)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Time-lapse Strategy */}
                {isTimeLapse && (
                  <div className="space-y-2">
                    <Label className="text-sm">Threshold Strategy</Label>
                    <Select value={thresholdStrategy} onValueChange={setThresholdStrategy}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_frame">Per-frame</SelectItem>
                        <SelectItem value="global">Global</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* 3D ROI Manager */}
          <div className="metric-card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-sm">3D ROI Manager</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addRoi}
                className="gap-1 text-xs h-8"
                data-testid="add-roi-btn"
              >
                <Plus className="w-3 h-3" />
                Add ROI
              </Button>
            </div>

            {/* ROI List */}
            <div className="space-y-3">
              {rois.map((roi) => (
                <div
                  key={roi.id}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                    activeRoiId === roi.id 
                      ? "bg-blue-500/10 border-blue-500/30" 
                      : "bg-secondary/30 border-white/5 hover:border-white/10"
                  }`}
                  onClick={() => {
                    setActiveRoiId(roi.id);
                    setRoiEnabled(true);
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: roi.color }}
                      />
                      <span className="text-sm font-medium">{roi.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateRoi(roi.id, { visible: !roi.visible });
                        }}
                        className="p-1 rounded hover:bg-white/10"
                      >
                        {roi.visible ? (
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        ) : (
                          <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRoi(roi.id);
                        }}
                        className="p-1 rounded hover:bg-red-500/20"
                        disabled={rois.length <= 1}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-red-400" />
                      </button>
                    </div>
                  </div>

                  {activeRoiId === roi.id && (
                    <div className="space-y-4 pt-2 border-t border-white/5">
                      {/* X Range - dual handle slider */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            X Range
                          </Label>
                          <span className="font-mono text-xs text-muted-foreground">
                            {roi.xRange[0]}% - {roi.xRange[1]}%
                          </span>
                        </div>
                        <Slider
                          value={roi.xRange}
                          onValueChange={(v) => updateRoi(roi.id, { xRange: v })}
                          min={0}
                          max={100}
                          step={1}
                          className="[&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-500 [&_[role=slider]]:w-4 [&_[role=slider]]:h-4 [&_[role=slider]]:shadow-[0_0_8px_rgba(239,68,68,0.5)]"
                        />
                      </div>

                      {/* Y Range - dual handle slider */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Y Range
                          </Label>
                          <span className="font-mono text-xs text-muted-foreground">
                            {roi.yRange[0]}% - {roi.yRange[1]}%
                          </span>
                        </div>
                        <Slider
                          value={roi.yRange}
                          onValueChange={(v) => updateRoi(roi.id, { yRange: v })}
                          min={0}
                          max={100}
                          step={1}
                          className="[&_[role=slider]]:bg-green-500 [&_[role=slider]]:border-green-500 [&_[role=slider]]:w-4 [&_[role=slider]]:h-4 [&_[role=slider]]:shadow-[0_0_8px_rgba(34,197,94,0.5)]"
                        />
                      </div>

                      {/* Z Range - dual handle slider with slice numbers (disabled in Time-lapse mode) */}
                      <div className={`space-y-2 ${isTimeLapse ? 'opacity-50' : ''}`}>
                        <div className="flex justify-between items-center">
                          <Label className="text-xs flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isTimeLapse ? 'bg-gray-400' : 'bg-blue-500'}`} />
                            Z Range (Slices)
                            {isTimeLapse && (
                              <span className="text-[10px] text-yellow-500 ml-1">(N/A in Time-lapse)</span>
                            )}
                          </Label>
                          <span className="font-mono text-xs text-muted-foreground">
                            {isTimeLapse ? (
                              <span className="text-muted-foreground/50">All frames</span>
                            ) : previewData?.depth ? (
                              <>
                                <span className="text-blue-400">
                                  {Math.round(roi.zRange[0] * previewData.depth / 100) + 1}
                                </span>
                                {" - "}
                                <span className="text-blue-400">
                                  {Math.round(roi.zRange[1] * previewData.depth / 100)}
                                </span>
                                <span className="text-muted-foreground/60"> of {previewData.depth}</span>
                              </>
                            ) : (
                              <>{roi.zRange[0]}% - {roi.zRange[1]}%</>
                            )}
                          </span>
                        </div>
                        <Slider
                          value={roi.zRange}
                          onValueChange={(v) => updateRoi(roi.id, { zRange: v })}
                          min={0}
                          max={100}
                          step={previewData?.depth ? Math.round(100 / previewData.depth) || 1 : 1}
                          disabled={isTimeLapse}
                          className={`[&_[role=slider]]:w-4 [&_[role=slider]]:h-4 ${
                            isTimeLapse 
                              ? '[&_[role=slider]]:bg-gray-400 [&_[role=slider]]:border-gray-400 [&_[role=slider]]:cursor-not-allowed' 
                              : '[&_[role=slider]]:bg-blue-500 [&_[role=slider]]:border-blue-500 [&_[role=slider]]:shadow-[0_0_8px_rgba(59,130,246,0.5)]'
                          }`}
                        />
                        {/* Quick slice selection buttons */}
                        {previewData?.depth && !isTimeLapse && (
                          <div className="flex gap-1 flex-wrap">
                            <button
                              onClick={() => updateRoi(roi.id, { zRange: [0, 100] })}
                              className="px-2 py-0.5 text-[10px] rounded bg-secondary hover:bg-secondary/80"
                            >
                              All
                            </button>
                            <button
                              onClick={() => updateRoi(roi.id, { zRange: [0, 50] })}
                              className="px-2 py-0.5 text-[10px] rounded bg-secondary hover:bg-secondary/80"
                            >
                              First Half
                            </button>
                            <button
                              onClick={() => updateRoi(roi.id, { zRange: [50, 100] })}
                              className="px-2 py-0.5 text-[10px] rounded bg-secondary hover:bg-secondary/80"
                            >
                              Second Half
                            </button>
                            <button
                              onClick={() => updateRoi(roi.id, { zRange: [25, 75] })}
                              className="px-2 py-0.5 text-[10px] rounded bg-secondary hover:bg-secondary/80"
                            >
                              Middle 50%
                            </button>
                          </div>
                        )}
                      </div>

                      {/* ROI Volume/Area indicator */}
                      <div className="flex items-center justify-between p-2 rounded bg-secondary/50 text-xs">
                        <span className="text-muted-foreground">
                          {isTimeLapse ? 'ROI Area:' : 'ROI Volume:'}
                        </span>
                        <span className="font-mono text-blue-400">
                          {isTimeLapse ? (
                            // In Time-lapse mode, only show XY area
                            <>
                              {(((roi.xRange[1] - roi.xRange[0]) * (roi.yRange[1] - roi.yRange[0])) / 100).toFixed(1)}%
                              <span className="text-muted-foreground/60 ml-1">(per frame)</span>
                            </>
                          ) : (
                            // In 3D mode, show full volume
                            <>
                              {(((roi.xRange[1] - roi.xRange[0]) * (roi.yRange[1] - roi.yRange[0]) * (roi.zRange[1] - roi.zRange[0])) / 10000).toFixed(1)}%
                              {previewData?.depth && (
                                <span className="text-muted-foreground/60 ml-1">
                                  ({Math.round((roi.zRange[1] - roi.zRange[0]) * previewData.depth / 100)} slices)
                                </span>
                              )}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - 3D Stack Viewer Preview */}
        <div className="metric-card p-4 h-fit">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" />
              <span className="font-medium text-sm">3D Stack Viewer</span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="h-8 w-28 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merged">Merged</SelectItem>
                  <SelectItem value="red">Red Only</SelectItem>
                  <SelectItem value="green">Green Only</SelectItem>
                </SelectContent>
              </Select>
              <button 
                className="p-1.5 rounded-lg hover:bg-white/10"
                onClick={() => {
                  if (activeRoi) {
                    updateRoi(activeRoi.id, { xRange: [25, 75], yRange: [25, 75] });
                  }
                }}
                title="Center ROI (50%)"
              >
                <Crosshair className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Canvas Preview */}
          <div className="relative aspect-square bg-slate-900 rounded-lg overflow-hidden mb-4">
            {previewData?.hasData ? (
              <canvas
                ref={canvasRef}
                width={400}
                height={400}
                className="w-full h-full"
                style={{ imageRendering: "pixelated" }}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                <Layers className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Load files to preview</p>
                <p className="text-xs opacity-60">ROI will be shown here</p>
              </div>
            )}
            
            {/* ROI indicator badge */}
            {roiEnabled && previewData?.hasData && (
              <div className="absolute top-3 left-3 px-2 py-1 rounded bg-blue-500/20 border border-blue-500/30">
                <span className="text-xs text-blue-400">ROI Active</span>
              </div>
            )}
          </div>

          {/* Z-Slice Navigation */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Z-Slice:</span>
              <span className="font-mono">{currentZ + 1} / {maxZ}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentZ(Math.max(0, currentZ - 1))}
                className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30"
                disabled={currentZ <= 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <Slider
                value={[currentZ]}
                onValueChange={([v]) => setCurrentZ(v)}
                max={maxZ - 1}
                step={1}
                className="flex-1"
                data-testid="z-preview-slider"
              />
              <button
                onClick={() => setCurrentZ(Math.min(maxZ - 1, currentZ + 1))}
                className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30"
                disabled={currentZ >= maxZ - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function DropZone({ channel, file, onDrop, onFileSelect, onClear }) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const isRed = channel === "red";
  
  // Reset input when file is cleared
  useEffect(() => {
    if (!file && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [file]);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDropEvent = (e) => {
    setIsDragging(false);
    onDrop(e);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvent}
      className={`
        relative p-4 rounded-xl border-2 border-dashed transition-all cursor-pointer
        ${isDragging ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/20"}
        ${file ? (isRed ? "border-red-500/50 bg-red-500/5" : "border-green-500/50 bg-green-500/5") : ""}
      `}
      data-testid={`${channel}-channel-dropzone`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".tif,.tiff"
        onChange={onFileSelect}
        className="absolute inset-0 opacity-0 cursor-pointer"
        data-testid={`${channel}-channel-input`}
      />
      
      <div className="flex flex-col items-center text-center">
        <div className={`
          w-10 h-10 mb-2 rounded-lg flex items-center justify-center
          ${isRed ? "bg-red-500/10" : "bg-green-500/10"}
        `}>
          <FileImage className={`w-5 h-5 ${isRed ? "text-red-500" : "text-green-500"}`} />
        </div>
        
        <p className={`text-sm font-medium mb-0.5 ${isRed ? "text-red-400" : "text-green-400"}`}>
          {isRed ? "Red" : "Green"} Channel
        </p>
        
        <p className="text-xs text-muted-foreground truncate max-w-full">
          {file ? file.name : "Drop TIFF file"}
        </p>
      </div>
      
      {file && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="absolute top-2 right-2 p-1 rounded hover:bg-white/10"
        >
          <Trash2 className="w-3 h-3 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}
